/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, call, takeEvery, put, select, takeLatest,
} from 'redux-saga/effects';

import mbpLogger from 'mbp-logger';

import { checkJWT } from '../../../Checkout/ducks/Global/Global-Operations';

import externalCartClient from '../../../../../apis/external-cart-apis/externalCartClient';
import {
    clearExternalCartItemsErrors,
    externalCartAddToCart,
    externalCartDeleteItem,
    externalCartUpdateDTW,
    externalCartUpdateTip,
    getExternalCartDTW, getExternalCartItems, loadExternalCartError, setExternalCartDTWById, setExternalCartError, setExternalCartItems,
} from './ExternalCart-Actions';
import { retrieveCart } from '../Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { getOrderId } from '../../../Checkout/ducks/Common/Common-Selectors';
import { trackEvent } from '../../../TagManager/ducks/TagManager/TagManager-Actions';
import { GenericErrorMessage } from '../../../Common/ducks/Error/Error-Operations';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { getFeatureFlags } from '../App/App-Selectors';

/**
 * Load the error that come fromr the serve and set them according to
 * the Feature in the sagas.
 * Call example:
 *  yield fork(workerLoadExternalCartError, {
        data: {
            itemId: null, // this will be null for the APIs cant access cartItemId, example: cart and payment
            featureType: 'cart', // values: cart, product, dtw, recipient, payment
            errors: ex.response?.data, // response from the API server
        },
    });
 * @param {data: object} action
 * @param {itemId: string, featureType: string, errors: object} data
 * @returns {object} set error into the sagas
 */

function formatMessageError(message) {
    let formattedMsg = message;
    if (formattedMsg) {
        formattedMsg = formattedMsg.replace('ship_address', 'Address');
        formattedMsg = formattedMsg.replace('is not valid', 'is not available');
        formattedMsg = formattedMsg.replace('ERROR_EXTERNAL_EXPIRED_ITEMS', 'Please select a future date and/or time to continue.');
    }

    return formattedMsg;
}

export function* workerLoadExternalCartError(action) {
    try {
        let errorObject = [];
        const {
            featureType, errors, itemId,
        } = action.data || {};

        const validationErrors = {};
        (errors.fieldErrors || []).forEach((errorLine) => {
            const keyItem = errorLine.fieldName;
            validationErrors[keyItem] = formatMessageError(errorLine.defaultMessage);
        });

        (errors.validationErrors || []).forEach((errorLine) => {
            const keyItem = errorLine.errorKey;
            validationErrors[keyItem] = formatMessageError(errorLine.errorMessage);
        });

        if (['cart', 'payment'].includes(featureType)) {
            const errorMsg = formatMessageError(errors.errorMessage);
            errorObject = {
                errorKey: errors.errorKey,
                errorMessage: errorMsg || `Error generated in ${featureType}`,
                validationErrors,
            };
        } else {
            errorObject[featureType] = {
                errorKey: errors.errorKey,
                errorMessage: formatMessageError(errors.errorMessage) || `Error generated in ${featureType}`,
                validationErrors,
            };
        }

        yield put(setExternalCartError({
            itemId,
            errors: errorObject,
            featureType,
        }));

        const errorEventObj = {
            eventCategory: 'External Cart Errors',
            eventAction: 'Checkout',
            eventLabel: errors.errorKey || featureType,
        };

        yield put(trackEvent(errorEventObj));

        mbpLogger.logError({
            function: 'workerLoadExternalCartError',
            appName: process.env.npm_package_name,
            module: 'ExternalCart-Operations',
            jsError: action?.data ? JSON.stringify(action.data) : 'Error trying load external cart errors',
            message: action?.data ? JSON.stringify(action.data) : 'Error trying load external cart errors',
        });
    } catch (ex) {
        const genericError = yield call(GenericErrorMessage);
        yield put(setExternalCartError(
            action.itemId,
            action.itemId,
            {
                error: {
                    errorMessage: genericError,
                },
            },
        ));
    }
}

function* watchLoadExternalCartError() {
    yield takeEvery(loadExternalCartError().type, workerLoadExternalCartError);
}

/**
 * Rebuild the data information needed for our system
 * where each product have their own information
 * @param {object} externalCartList
 * @returns {object} buildProductList
 */
function reBuildProductsExternalCart(listItemsExtCart, isSingleRBRetailerEnabled) {
    const externalCartList = { ...listItemsExtCart };

    if (externalCartList) {
        if (externalCartList.products_order?.length > 0) {
            const buildProductList = [];
            const retailerProducts = [];
            // Re-build the data is need for each product in our system
            // Add in the product the group is it to get the prices
            externalCartList.products_order.forEach((key) => {
                const productItem = { ...externalCartList.products[key] };
                const itemProd = {
                    id: productItem.id,
                    title: productItem.title?.[0] || '',
                    imageURL: productItem.main_image?.[0] || '',
                    productType: productItem.product_type?.[0]?.name || '',
                    description: productItem.product_description?.[0] || '',
                };

                // Add the responses group that provide the price of the product.
                // and shipping method id into onDemand
                if (externalCartList.responses) {
                    Object.keys(externalCartList.responses).forEach((keyResp) => {
                        const itemResp = externalCartList.responses[keyResp];

                        if (itemResp.aliases && itemResp.aliases.length > 0) {
                            if (itemResp.aliases.includes(Number(itemProd.id))) {
                                const positionProd = itemResp.aliases.indexOf(Number(itemProd.id));

                                const onDemandItem = itemResp?.products?.non_engraving?.on_demand?.[positionProd] || {};
                                itemProd.variantId = onDemandItem.variant?.id;
                                itemProd.itemVolume = onDemandItem.product?.item_volume || '';
                                itemProd.price = parseFloat(onDemandItem.variant?.price || 0).toFixed(2);
                                itemProd.retailer = {
                                    id: onDemandItem.retailer.id,
                                    name: onDemandItem.retailer.name || '',
                                };
                                if (!retailerProducts[onDemandItem.retailer.id]) {
                                    retailerProducts[onDemandItem.retailer.id] = [productItem.id];
                                } else {
                                    retailerProducts[onDemandItem.retailer.id].push(productItem.id);
                                }
                                itemProd.deliveryMethodId = onDemandItem.retailer.shipping_method_id || null;
                            }
                        }
                    });
                }
                // TODO: review if this fragment need remove since we are try get
                // delivery date another API when the product is selected
                // if (externalCartList?.entities?.shipping_methods) {
                //     Object.keys(externalCartList.entities.shipping_methods).forEach((keySM) => {
                //         const itemShipMethod = externalCartList.entities.shipping_methods[keySM];
                //         if (
                //             itemProd.deliveryMethodId
                //             && itemShipMethod?.next_delivery
                //             && Number(itemProd.deliveryMethodId) === Number(keySM)
                //         ) {
                //             itemProd.shippingMethods = itemShipMethod;
                //         }
                //     });
                // }

                if (itemProd.deliveryMethodId && itemProd.price > 0) {
                    buildProductList.push(itemProd);
                }
            });

            // Thist will sort the total product for each retailer
            let retailerProd = {};
            let retailerIdWithMoreElements = null;
            if (isSingleRBRetailerEnabled) {
                Object.keys(retailerProducts)
                    .forEach((retailerKey) => {
                        if (!retailerProd.key) {
                            retailerProd = { key: retailerKey, size: retailerProducts[retailerKey].length };
                        } else if (retailerProducts[retailerKey].length > retailerProd.size) {
                            retailerProd = { key: retailerKey, size: retailerProducts[retailerKey].length };
                        }
                    });

                // Get the retailer that have more items in the list from the response
                retailerIdWithMoreElements = retailerProd?.key || null;
            }

            // Filter the list with the retailer that have more products.
            let filteredProductList = buildProductList;
            if (retailerIdWithMoreElements) {
                filteredProductList = buildProductList
                    .filter(
                        (item) => String(item.retailer.id) === String(retailerIdWithMoreElements),
                    );
            }

            return filteredProductList;
        }
    }
    return [];
}

/**
 * Worker that get the catalog of product can be buy
 * for the user
 * @param {action} data to search the catalog list
 * @returns {object} is set into Redux for ui component have access the information
 */
function* workerGetExternalCartItem(action) {
    try {
        yield put(clearExternalCartItemsErrors({
            itemId: null,
            featureType: 'cart',
        }));
        const jwtToken = yield call(checkJWT);

        const response = yield call(externalCartClient.getCatalogSelect, {
            jwtToken, payload: action.data,
        });

        if (response.status === 201 || response.status === 200) {
            const featureFlags = yield select(getFeatureFlags);
            const isSingleRBRetailerEnabled = featureFlags['is-single-rb-retailer-enabled'] || false;

            const productList = reBuildProductsExternalCart(response.data, isSingleRBRetailerEnabled);
            yield put(setExternalCartItems(productList));

            if (productList?.length === 0) {
                yield fork(workerLoadExternalCartError, {
                    data: {
                        itemId: null,
                        featureType: 'cart',
                        errors: {
                            errorKey: 'CATALOG_SELECT_EMPTY',
                            errorMessage: 'No retailers found in this area',
                        },
                    },
                });
            } else if (action.data?.handleCallBack) {
                yield call(action.data.handleCallBack, false);
            }
        } else {
            yield put(setExternalCartItems([]));
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerCartServiceExternalCart',
            jsError: ex,
            message: 'Error occured while getting External cart Items',
        });

        yield fork(workerLoadExternalCartError, {
            data: {
                itemId: null,
                featureType: 'cart',
                errors: {
                    errorKey: 'CATALOG_SELECT_ERROR',
                    errorMessage: ex.response?.data?.error,
                },
            },
        });
        yield put(setExternalCartItems([]));
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchExternalCartItem() {
    yield takeLatest(getExternalCartItems().type, workerGetExternalCartItem);
}

// Get the delivery time accorfing of the delivery method from the retailer
function* workerGetExternalCartDTW(action) {
    try {
        const jwtToken = yield call(checkJWT);
        const deliveryMethodId = action.data.deliveryMethodId;

        // TODO Validate is the clear data have cart item or not.
        yield put(clearExternalCartItemsErrors());

        if (deliveryMethodId) {
            const response = yield call(externalCartClient.getExternalCartDTW, jwtToken, deliveryMethodId);
            if (response.status === 201 || response.status === 200) {
                yield put(setExternalCartDTWById({ [deliveryMethodId]: response.data }));
                if (action.data?.handleCallBack && action.data.isOpen) {
                    yield call(action.data.handleCallBack, true);
                }
                if (action.data?.handleLoading) {
                    yield call(action.data.handleLoading, '');
                }
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerCartServiceExternalCart',
            jsError: ex,
            message: 'Error occured while getting Delivery time window in External cart Items',
        });

        if (action.data?.handleLoading) {
            yield call(action.data.handleLoading, '');
        }
        // TODO add the order item id if it exist or method id
        yield fork(workerLoadExternalCartError, {
            data: {
                itemId: action.data?.deliveryMethodId,
                featureType: 'dtw',
                errors: ex.response?.data,
            },
        });
    }
}

function* watchGetExternalCartDTW() {
    yield takeEvery(getExternalCartDTW().type, workerGetExternalCartDTW);
}
/**
 * Add to cart products for reserve bar.
 *
 * @param {object} externalCartList
 * @returns {object} set if was successfully added
 */
function* workerExternalCartAddToCart(action) {
    try {
        yield put(updateShowLoadingMessage());
        yield put(clearExternalCartItemsErrors({
            itemId: null,
            featureType: 'cart',
        }));
        const jwtToken = yield call(checkJWT);

        const responseCart = yield call(externalCartClient.externalCartAddToCart, { env: {}, jwtToken, cartData: action.data.cartData });

        if (responseCart.status === 201 || responseCart.status === 200) {
            yield call(retrieveCart);

            yield put(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Add to cart',
                eventLabel: 'Reservebar Products',
            }));

            if (action.data?.handleCallBack) {
                yield call(action.data.handleCallBack, false);
            }
        }

        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerCartServiceExternalCart',
            jsError: ex,
            message: 'Error occured while added to Cart the External cart Items',
        });

        yield fork(workerLoadExternalCartError, {
            data: {
                itemId: null,
                featureType: 'cart',
                errors: ex.response?.data,
            },
        });

        yield put(flagOffShowLoadingMessage());
    }
}

function* watchExternalCartAddToCart() {
    yield takeEvery(externalCartAddToCart().type, workerExternalCartAddToCart);
}

/**
 * @decription Delete product for reserve bar.
 *
 * @param {string} extCartId
 * @param {string} variantId
 * @returns {object} set if was successfully added
 */
function* workerExternalDeleteItem(action) {
    try {
        yield put(updateShowLoadingMessage());
        yield put(clearExternalCartItemsErrors());
        const jwtToken = yield call(checkJWT);
        const orderId = yield select(getOrderId);

        const responseCart = yield call(externalCartClient.externalCartDeleteItem, {
            env: {}, jwtToken, orderId, extCartItemId: action.data.extCartItemId,
        });

        if (responseCart.status === 201 || responseCart.status === 200) {
            yield call(retrieveCart);
        }

        if (action.data?.handleCallBack) {
            yield call(action.data.handleCallBack, '');
        }

        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerCartServiceExternalCart',
            jsError: ex,
            message: 'Error occured while added to Cart the External cart Items',
        });

        if (action.data?.handleCallBack) {
            yield call(action.data.handleCallBack, false);
        }

        yield fork(workerLoadExternalCartError, {
            data: {
                itemId: action.data.extCartItemId,
                featureType: 'product',
                errors: ex.response?.data,
            },
        });
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchExternalCartDeleteItem() {
    yield takeEvery(externalCartDeleteItem().type, workerExternalDeleteItem);
}

/**
 * @decription Update delivery date for specific product on reserve bar.
 *
 * @param {string} deliveryStartDate
 * @param {string} deliveryEndDate
 * @param {string} deliverySLA
 * @returns {object} set if was successfully added
 */
function* workerExternaCartUpdateDTW(action) {
    try {
        yield put(updateShowLoadingMessage());
        yield put(clearExternalCartItemsErrors());
        const jwtToken = yield call(checkJWT);
        const orderId = yield select(getOrderId);

        const responseCart = yield call(externalCartClient.externalCartUpdateDTW, {
            env: {},
            jwtToken,
            orderId,
            extCartItemId: action.data.extCartItemId,
            deliveryData: action.data.deliveryData,
        });

        if (responseCart.status === 201 || responseCart.status === 200) {
            yield call(retrieveCart);
        }

        if (action.data?.handleCallBack) {
            yield call(action.data.handleCallBack, '');
        }

        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerCartServiceExternalCart',
            jsError: ex,
            message: 'Error occured while added to Cart the External cart Items',
        });

        if (action.data?.handleCallBack) {
            yield call(action.data.handleCallBack, false);
        }

        yield fork(workerLoadExternalCartError, {
            data: {
                itemId: action.data.extCartItemId,
                featureType: 'dtw',
                errors: ex.response?.data,
            },
        });

        yield put(flagOffShowLoadingMessage());
    }
}

function* watchExternalCartUpdateDTW() {
    yield takeEvery(externalCartUpdateDTW().type, workerExternaCartUpdateDTW);
}

/**
 * @decription Delete product for reserve bar.
 *
 * @param {string} extCartId
 * @param {string} variantId
 * @returns {object} set if was successfully added
 */
function* workerExternalCartUpdateTip(action) {
    try {
        yield put(updateShowLoadingMessage());
        yield put(clearExternalCartItemsErrors());
        const jwtToken = yield call(checkJWT);
        const cartId = yield select(getOrderId);

        if (action.data.tipAmount && cartId) {
            const responseCart = yield call(externalCartClient.externalCartUpdateTip, {
                env: {}, jwtToken, cartId, tipAmount: action.data.tipAmount,
            });

            if (responseCart.status === 201 || responseCart.status === 200) {
                yield call(retrieveCart);
            }

            if (action.data?.handleCallBack) {
                yield call(action.data.handleCallBack, '');
            }
        }
        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'workerExternalCartUpdateTip',
            jsError: ex,
            message: 'Error occured while update Tip in the External cart',
        });

        if (action.data?.handleCallBack) {
            yield call(action.data.handleCallBack, false);
        }

        yield fork(workerLoadExternalCartError, {
            data: {
                itemId: null,
                featureType: 'payment',
                errors: ex.response?.data,
            },
        });
        yield call(retrieveCart);
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchExternalCartUpdateTip() {
    yield takeEvery(externalCartUpdateTip().type, workerExternalCartUpdateTip);
}

const watchers = [
    fork(watchExternalCartAddToCart),
    fork(watchExternalCartItem),
    fork(watchGetExternalCartDTW),
    fork(watchExternalCartDeleteItem),
    fork(watchExternalCartUpdateDTW),
    fork(watchLoadExternalCartError),
    fork(watchExternalCartUpdateTip),
];

export {
    watchers,
};

export default {};
